<template>
  <footer class="azad-footer" :class="theme">

    <div class="footer-accent"></div>

    <div class="footer-main">
      <div class="container">
        <div class="footer-grid">

          <!-- Brand column -->
          <div class="footer-brand">
            <div class="brand-logo">
              <img
                v-if="hasLogo"
                :src="`${urlBase}/${systemSettings.logo}`"
                :alt="systemSettings.brand_name"
                class="brand-logo-img"
                @error="handleLogoError"
              />
            </div>
            <h2 class="brand-name">{{ brandNameLine1 }}<br /><span>{{ brandNameLine2 }}</span></h2>
            <p class="brand-tagline">{{ systemSettings.tagline }}</p>
            <p class="brand-desc">{{ systemSettings.short_description }}</p>
          </div>

          <!-- Quick links -->
          <div class="footer-col">
            <h4 class="footer-col-title">দ্রুত লিঙ্ক</h4>
            <ul class="footer-links">
              <li v-for="link in quickLinks" :key="link.label">
                <router-link :to="link.to">
                  <span class="link-arrow">›</span>{{ link.label }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Contact -->
          <div class="footer-col">
            <h4 class="footer-col-title">যোগাযোগ করুন</h4>
            <ul class="contact-list">
              <li v-if="systemSettings.phone">
                <div class="contact-icon" v-html="icons.phone"></div>
                <div class="contact-body">
                  <span class="contact-label">ফোন</span>
                  <a :href="`tel:${systemSettings.phone}`" class="contact-value">{{ systemSettings.phone }}</a>
                </div>
              </li>
              <li v-if="systemSettings.email">
                <div class="contact-icon" v-html="icons.email"></div>
                <div class="contact-body">
                  <span class="contact-label">ইমেইল</span>
                  <a :href="`mailto:${systemSettings.email}`" class="contact-value">{{ systemSettings.email }}</a>
                </div>
              </li>
              <li v-if="systemSettings.address">
                <div class="contact-icon" v-html="icons.location"></div>
                <div class="contact-body">
                  <span class="contact-label">কেন্দ্রীয় কার্যালয়</span>
                  <span class="contact-value">
                    {{ systemSettings.address }}, {{ systemSettings.city }}, {{ systemSettings.state }} {{ systemSettings.zip_code }}
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <!-- Social & office hours -->
          <div class="footer-col">
            <h4 class="footer-col-title">আমাদের অনুসরণ করুন</h4>
            <div class="social-grid">
              <a
                v-if="systemSettings.facebook"
                :href="systemSettings.facebook"
                class="social-btn fb"
                target="_blank" rel="noopener"
              >
                <span class="social-icon" v-html="icons.facebook"></span>
                <span class="social-label">Facebook</span>
              </a>
              <a
                v-if="systemSettings.twitter"
                :href="systemSettings.twitter"
                class="social-btn tw"
                target="_blank" rel="noopener"
              >
                <span class="social-icon" v-html="icons.twitter"></span>
                <span class="social-label">Twitter / X</span>
              </a>
              <a
                v-if="systemSettings.youtube"
                :href="systemSettings.youtube"
                class="social-btn yt"
                target="_blank" rel="noopener"
              >
                <span class="social-icon" v-html="icons.youtube"></span>
                <span class="social-label">YouTube</span>
              </a>
              <a
                v-if="systemSettings.instagram"
                :href="systemSettings.instagram"
                class="social-btn ig"
                target="_blank" rel="noopener"
              >
                <span class="social-icon" v-html="icons.instagram"></span>
                <span class="social-label">Instagram</span>
              </a>
            </div>

            <div class="footer-divider" v-if="systemSettings.business_hours"></div>

            <div class="office-hours" v-if="systemSettings.business_hours">
              <h5 class="office-title">কার্যালয়ের সময়</h5>
              <p v-for="(line, i) in businessHoursLines" :key="i" :class="{ closed: line.includes('বন্ধ') }">
                {{ line }}
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Bottom strip -->
    <div class="footer-bottom">
      <div class="container footer-bottom-inner">
        <p class="footer-copy">
          © ২০২৫ {{ systemSettings.brand_name }}। সর্বস্বত্ব সংরক্ষিত।
        </p>
        <div class="footer-bottom-links">
          <a href="#">গোপনীয়তা নীতি</a>
          <span class="sep">·</span>
          <a href="#">শর্তাবলী</a>
        </div>
      </div>
    </div>

  </footer>
</template>

<script setup>
import { inject, ref, computed, onMounted } from 'vue';
import axios from 'axios';

const theme   = inject('theme', ref('dark'));
const apiBase = ref(process.env.VUE_APP_BASE_API);
const urlBase = ref(process.env.VUE_APP_BASE_URL);

const systemSettings = ref({
  brand_name: '', tagline: '', short_description: '',
  logo: '', phone: '', email: '',
  address: '', city: '', state: '', zip_code: '',
  facebook: '', twitter: '', youtube: '', instagram: '',
  business_hours: '',
});

const hasLogo = computed(() => !!systemSettings.value.logo?.trim());

// Split "বাংলাদেশ আজাদ পার্টি" into two lines for styling
const brandNameLine1 = computed(() => {
  const parts = systemSettings.value.brand_name?.split(' ') || [];
  return parts[0] || '';
});
const brandNameLine2 = computed(() => {
  const parts = systemSettings.value.brand_name?.split(' ') || [];
  return parts.slice(1).join(' ') || '';
});

const businessHoursLines = computed(() =>
  systemSettings.value.business_hours?.split('\n').map(l => l.trim()).filter(Boolean) || []
);

const handleLogoError = (e) => { e.target.style.display = 'none'; };

const loadSystemSettings = async () => {
  try {
    const { data } = await axios.get(`${apiBase.value}/system`);
    if (data?.system) systemSettings.value = data.system;
  } catch (err) {
    console.error('Failed to load system settings:', err);
  }
};

onMounted(loadSystemSettings);

const quickLinks = [
  { label: 'নীতি ও আদর্শ',        to: '/principles' },
  { label: '১৭ দফা',              to: '/the-seventeen-points' },
  { label: 'আজাদ বার্তা',         to: '/azad-barta' },
  { label: 'জনতার মতামত',         to: '/opinions' },
  { label: 'জুলাই যোদ্ধা তালিকা', to: '/july-fighters' },
  { label: 'জিজ্ঞাসা',            to: '/faq' },
  { label: 'সদস্য হন',            to: '/membership' },
];

const icons = {
  phone:    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  email:    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  location: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="2"/></svg>`,
  facebook: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>`,
  twitter:  `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,
  youtube:  `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#0d0d0d"/></svg>`,
  instagram:`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>`,
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Noto+Sans+Bengali:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.azad-footer {
  --red:              #C8102E;
  --green:            #006B3F;
  --gold:             #c8a84b;
  --gold-light:       #f0d080;
  --footer-bg:        #0d0d0d;
  --footer-bg-main:   #161616;
  --footer-border:    rgba(255, 255, 255, 0.05);
  --footer-divider:   rgba(255, 255, 255, 0.06);
  --text:             #f0ede6;
  --text2:            rgba(240, 237, 230, 0.65);
  --text3:            rgba(240, 237, 230, 0.35);
  --col-title-border: rgba(255, 255, 255, 0.06);
  --contact-icon-bg:  rgba(200, 16, 46, 0.12);
  --contact-icon-border: rgba(200, 16, 46, 0.25);
  --social-bg:        rgba(255, 255, 255, 0.03);
  --social-border:    rgba(255, 255, 255, 0.07);
  --social-border-hover: rgba(255, 255, 255, 0.15);
  font-family: 'Noto Sans Bengali', sans-serif;
  background: var(--footer-bg);
  color: var(--text);
  transition: background 0.3s ease, color 0.3s ease;
}

.azad-footer.light {
  --footer-bg:        #f0ebe0;
  --footer-bg-main:   #f7f3ea;
  --footer-border:    rgba(0, 0, 0, 0.08);
  --footer-divider:   rgba(0, 0, 0, 0.08);
  --text:             #1a1208;
  --text2:            rgba(30, 20, 5, 0.7);
  --text3:            rgba(30, 20, 5, 0.4);
  --col-title-border: rgba(0, 0, 0, 0.08);
  --contact-icon-bg:  rgba(200, 16, 46, 0.08);
  --contact-icon-border: rgba(200, 16, 46, 0.2);
  --social-bg:        rgba(0, 0, 0, 0.03);
  --social-border:    rgba(0, 0, 0, 0.1);
  --social-border-hover: rgba(0, 0, 0, 0.2);
  --gold:             #8a6a10;
  --gold-light:       #7a5a08;
}

.container { max-width: 1140px; margin: 0 auto; padding: 0 28px; }

.footer-accent {
  height: 3px;
  background: linear-gradient(90deg, var(--green) 0%, var(--red) 40%, var(--gold) 70%, var(--green) 100%);
}

.footer-main { padding: 64px 0 48px; background: var(--footer-bg-main); transition: background 0.3s ease; }

.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1.4fr 1.4fr;
  gap: 48px;
}

/* ── Brand ── */
.brand-logo { margin-bottom: 16px; }
.brand-logo-img { height: 72px; width: auto; object-fit: contain; }

.brand-name { font-size: 22px; font-weight: 700; color: var(--text); line-height: 1.25; margin-bottom: 8px; transition: color 0.3s ease; }
.brand-name span { color: var(--gold-light); }
.brand-tagline { font-size: 12px; letter-spacing: 1.5px; color: var(--text3); margin-bottom: 14px; transition: color 0.3s ease; }
.brand-desc { font-size: 13px; color: var(--text2); line-height: 1.85; transition: color 0.3s ease; }

/* ── Column titles ── */
.footer-col-title {
  font-size: 11px; letter-spacing: 3px; color: var(--gold);
  text-transform: uppercase; font-family: Georgia, serif;
  margin-bottom: 22px; padding-bottom: 12px;
  border-bottom: 1px solid var(--col-title-border);
  transition: color 0.3s ease, border-color 0.3s ease;
}

/* ── Quick links ── */
.footer-links { list-style: none; display: flex; flex-direction: column; gap: 10px; }
.footer-links li a { font-size: 13.5px; color: var(--text2); text-decoration: none; display: flex; align-items: center; gap: 8px; transition: color 0.2s; }
.footer-links li a:hover { color: var(--gold-light); }
.link-arrow { color: var(--red); font-size: 16px; line-height: 1; transition: transform 0.2s; }
.footer-links li a:hover .link-arrow { transform: translateX(3px); }

/* ── Contact ── */
.contact-list { list-style: none; display: flex; flex-direction: column; gap: 18px; }
.contact-list li { display: flex; gap: 12px; align-items: flex-start; }
.contact-icon { width: 32px; height: 32px; border-radius: 50%; background: var(--contact-icon-bg); border: 1px solid var(--contact-icon-border); display: flex; align-items: center; justify-content: center; color: var(--red); flex-shrink: 0; margin-top: 2px; transition: background 0.3s ease; }
.contact-body { display: flex; flex-direction: column; gap: 2px; }
.contact-label { font-size: 10px; letter-spacing: 1.5px; color: var(--text3); text-transform: uppercase; transition: color 0.3s ease; }
.contact-value { font-size: 13px; color: var(--text2); text-decoration: none; line-height: 1.6; transition: color 0.2s; }
a.contact-value:hover { color: var(--gold-light); }

/* ── Social ── */
.social-grid { display: flex; flex-direction: column; gap: 10px; }
.social-btn { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border-radius: 4px; text-decoration: none; border: 1px solid var(--social-border); background: var(--social-bg); transition: all 0.22s; position: relative; overflow: hidden; }
.social-btn::before { content: ''; position: absolute; inset: 0; opacity: 0; transition: opacity 0.22s; }
.social-btn.fb::before { background: rgba(24, 119, 242, 0.12); }
.social-btn.tw::before { background: rgba(29, 161, 242, 0.1); }
.social-btn.yt::before { background: rgba(255, 0, 0, 0.1); }
.social-btn.ig::before { background: rgba(214, 48, 122, 0.1); }
.social-btn:hover::before { opacity: 1; }
.social-btn:hover { border-color: var(--social-border-hover); transform: translateX(3px); }
.social-icon { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; position: relative; z-index: 1; }
.social-btn.fb .social-icon { background: #1877f2; color: #fff; }
.social-btn.tw .social-icon { background: #000; color: #fff; }
.social-btn.yt .social-icon { background: #ff0000; color: #fff; }
.social-btn.ig .social-icon { background: linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888); color: #fff; }
.social-label { font-size: 13px; font-weight: 600; color: var(--text); position: relative; z-index: 1; transition: color 0.3s ease; }

/* ── Office hours ── */
.footer-divider { height: 1px; background: var(--footer-divider); margin: 22px 0; transition: background 0.3s ease; }
.office-title { font-size: 11px; letter-spacing: 2px; color: var(--gold); text-transform: uppercase; font-family: Georgia, serif; margin-bottom: 10px; transition: color 0.3s ease; }
.office-hours p { font-size: 13px; color: var(--text2); line-height: 1.9; transition: color 0.3s ease; }
.office-hours .closed { color: var(--text3); font-size: 12px; }

/* ── Bottom ── */
.footer-bottom { background: var(--footer-bg); border-top: 1px solid var(--footer-border); padding: 18px 0; transition: background 0.3s ease, border-color 0.3s ease; }
.footer-bottom-inner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.footer-copy { font-size: 12px; color: var(--text3); letter-spacing: 0.3px; transition: color 0.3s ease; }
.footer-bottom-links { display: flex; align-items: center; gap: 10px; }
.footer-bottom-links a { font-size: 12px; color: var(--text3); text-decoration: none; transition: color 0.2s; }
.footer-bottom-links a:hover { color: var(--gold-light); }
.footer-bottom-links .sep { color: var(--text3); font-size: 10px; }

/* ── Responsive ── */
@media (max-width: 1024px) { .footer-grid { grid-template-columns: 1fr 1fr; gap: 40px; } }
@media (max-width: 600px) {
  .footer-grid { grid-template-columns: 1fr; gap: 36px; }
  .footer-main { padding: 48px 0 36px; }
  .footer-bottom-inner { flex-direction: column; align-items: flex-start; }
}
</style>